import EmailTemplatePreviewDialog from '@/components/EmailTemplatePreviewDialog'
import LabelSelectList from '@/components/LabelSelectList'
import PermissionsController from '@/components/PermissionsController'
import Redirect from '@/components/Redirect'
import TableTabButton from '@/components/TableTabButton'
import usePermissions from '@/hooks/usePermissions'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import AddCircle from '@mui/icons-material/AddCircle'
import Edit from '@mui/icons-material/Edit'
import Close from '@mui/icons-material/HighlightOff'
import Preview from '@mui/icons-material/Preview'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

const mapStateToProps = ({ emailTemplate, loading }) => ({
  emailTemplateList: emailTemplate.emailTemplateList,
  emailTemplateLabels: emailTemplate.emailTemplateLabels,
  isLoading: loading['emailTemplate/getEmailTemplates'],
  isLabelsLoading: loading['emailTemplate/getEmailTemplateLabels'],
  deleteLoading: loading['emailTemplate/deleteEmailTemplate'],
})

const fieldMap = {
  name: 'EMAIL_TEMP_NAME',
  subject: 'EMAIL_TEMP_SUBJECT',
  labelNames: 'EMAIL_TEMP_LABELNAMES',
  contain: 'EMAIL_TEMP_CONTAIN',
  difficulty: 'EMAIL_TEMP_DIFFICULTY',
  lastUpdateTime: 'EMAIL_TEMP_LAST_UPDATE_TIME',
}

const PhishEmailTemplate = ({
  dispatch,
  emailTemplateList,
  emailTemplateLabels,
  isLoading,
  isLabelsLoading,
  deleteLoading,
}) => {
  const { data, paging = {} } = emailTemplateList || {}
  const { data: labelData, paging: labelPaging = {} } = emailTemplateLabels

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [sort, setSort] = useState([{ field: 'lastUpdateTime', sort: 'desc' }])
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [labelPage, setLabelPage] = useState(0)
  const [deleteEmailTemplateData, setDeleteEmailTemplateData] = useState()
  const [deleteLabelData, setDeleteLabelData] = useState()
  const [previewEmailTemplateId, setPreviewEmailTemplateId] = useState()
  const [selectedLabel, setSelectedLabel] = useState([])

  const routeParams = useParams()
  const { type = '' } = routeParams || {}

  const isSystem = type === 'system'
  const isSystemRef = useRef(isSystem)

  const canCreate = usePermissions(isSystem ? 'emailTempSystem_create' : 'emailTempPerson_create')
  const canModify = usePermissions(isSystem ? 'emailTempSystem_modify' : 'emailTempPerson_modify')
  const canDelete = usePermissions(isSystem ? 'emailTempSystem_delete' : 'emailTempPerson_delete')
  const canView = usePermissions(isSystem ? 'emailTempSystem_view' : 'emailTempPerson_view')

  const companyId = useSelector(state => state.auth.account?.companyId)

  useEffect(() => {
    if (isSystem !== isSystemRef) {
      setPage(0)
      setSort([{ field: 'lastUpdateTime', sort: 'desc' }])
      setPageSize(10)
    }
  }, [isSystem])

  const form = useRef()

  const pagination = {
    ...paging,
    page,
    pageSize,
    onPageChange: setPage,
    onPageSizeChange: setPageSize,
  }

  const btnProps = {
    size: 'small',
    sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
  }
  const iconProps = {
    sx: {
      padding: 0.5,
    },
  }

  const cols = [
    {
      field: 'name',
      headerName: t('general.template_name'),
      width: 200,
    },
    {
      field: 'subject',
      headerName: t('general.mail_subject'),
      width: 200,
    },
    {
      field: 'labelNames',
      headerName: t('general.label'),
      width: 100,
    },
    {
      field: 'contain',
      headerName: t('general.contain'),
      width: 100,
    },
    ...(isSystem
      ? [
          {
            field: 'difficulty',
            headerName: t('general.difficulty'),
            valueFormatter: ({ value }) => (value ? t(`general.${value?.toLowerCase?.()}`) : '-'),
            width: 120,
          },
        ]
      : []),
    {
      field: 'lastUpdateTime',
      headerName: t('general.update_date'),
      width: 200,
      valueFormatter: ({ value }) => (value ? moment(value).format(DATE_TIME_FORMAT) : '-'),
    },
    {
      field: 'edit',
      hidden: !canModify,
      headerName: t('general.edit'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            navigate(`/phish_template_email/edit/`, {
              state: { ...row, type: isSystem ? 'system' : 'individual' },
            })
          }}
        >
          <Edit {...iconProps} />
        </IconButton>
      ),
    },
    {
      field: 'preview',
      headerName: t('general.preview'),
      width: 100,
      renderCell: ({ row }) => (
        <IconButton
          {...btnProps}
          onClick={() => {
            setPreviewEmailTemplateId(row?.emailTempId)
          }}
        >
          <Preview {...iconProps} />
        </IconButton>
      ),
    },
    // {
    //   field: 'delete',
    //   hidden: !canDelete,
    //   headerName: t('general.delete'),
    //   width: 100,
    //   renderCell: ({ row }) => (
    //     <IconButton
    //       {...btnProps}
    //       disabled={isSystem && companyId !== 1}
    //       onClick={() => {
    //         setDeleteEmailTemplateData(row)
    //       }}
    //     >
    //       <Close {...iconProps} />
    //     </IconButton>
    //   ),
    // },
    isSystem && companyId !== 1
      ? null
      : {
          field: 'delete',
          hidden: !canDelete,
          headerName: t('general.delete'),
          width: 100,
          renderCell: ({ row }) => (
            <IconButton {...btnProps} onClick={() => setDeleteEmailTemplateData(row)}>
              <Close {...iconProps} />
            </IconButton>
          ),
        },
  ].filter(Boolean)

  const fetchData = useCallback(() => {
    const formValues = form.current?.getValues()
    const { search } = formValues || {}

    const payload = {
      searchParam: search,
      labelId: selectedLabel,
      sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
      pageIndex: page,
      pageSize,
      type: isSystem ? 'S' : 'I',
    }

    dispatch({
      type: 'emailTemplate/getEmailTemplates',
      payload,
    })
  }, [dispatch, sort, page, pageSize, selectedLabel, isSystem, form])

  const deleteEmailTemplate = () => {
    if (deleteEmailTemplateData) {
      dispatch({
        type: 'emailTemplate/deleteEmailTemplate',
        payload: { emailTempId: deleteEmailTemplateData.emailTempId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_template_deleted', {
                email_template: deleteEmailTemplateData.name,
              }),
              severity: 'success',
            },
          })
          setDeleteEmailTemplateData()
          fetchData()
        },
      })
    }
  }

  const getLabels = () => {
    dispatch({
      type: 'emailTemplate/getEmailTemplateLabels',
      payload: { type: isSystem ? 'S' : 'I', pageIndex: labelPage, pageSize: 10 },
    })
  }

  const deleteEmailTemplateLabel = () => {
    if (deleteLabelData) {
      const { labelId, labelName } = deleteLabelData
      dispatch({
        type: 'emailTemplate/deleteEmailTemplateLabel',
        payload: { labelId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.email_template_deleted', {
                email_template: labelName,
              }),
              severity: 'success',
            },
          })
          setDeleteLabelData()
          getLabels()
        },
      })
    }
  }

  useEffect(() => {
    fetchData()
  }, [sort, page, pageSize, fetchData])

  useEffect(() => {
    getLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystem, labelPage, dispatch])

  if (!canView) {
    return <Redirect />
  }

  return (
    <Container maxWidth={false} sx={{ flex: 1, display: 'flex' }}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Grid container>
            <Grid item>
              <FormCreator
                ref={form}
                formItems={[
                  ...(canCreate && (companyId === 1 || !isSystem)
                    ? [
                        {
                          component: () => (
                            <Button
                              variant="text"
                              color="secondary_80"
                              endIcon={<AddCircle />}
                              onClick={() => {
                                navigate(
                                  `/phish_template_email/${
                                    isSystem ? 'system' : 'individual'
                                  }/create`,
                                )
                              }}
                            >
                              {t('general.add_template')}
                            </Button>
                          ),
                        },
                      ]
                    : []),
                  {
                    label: t('general.quick_search'),
                    name: 'search',
                    defaultValue: '',
                    inputConfig: {
                      onSubmit: async () => {
                        setPage(0)
                        fetchData()
                      },
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />
            </Grid>
          </Grid>

          <Grid container alignItems="center" justifyContent="space-between" py={2}>
            <Grid item>
              <LabelSelectList
                idKey="labelId"
                nameKey="labelName"
                options={labelData ?? []}
                paging={labelPaging}
                page={labelPage}
                onPageChange={val => {
                  if (!isLabelsLoading) {
                    setLabelPage(val)
                  }
                }}
                onDelete={setDeleteLabelData}
                selected={selectedLabel}
                onSelect={label => {
                  if (selectedLabel === label) {
                    setSelectedLabel()
                  } else {
                    setSelectedLabel(label)
                  }
                }}
              />
            </Grid>
            <Grid item>
              <Stack direction="row">
                <Box flex={1} />
                <PermissionsController permission={'emailTempSystem_view'}>
                  <TableTabButton
                    selected={isSystem}
                    onClick={() => {
                      navigate('/phish_template_email/system', { replace: true })
                      setSelectedLabel([])
                      setLabelPage(0)
                    }}
                    label={t('menu.system')}
                  />
                </PermissionsController>
                <PermissionsController permission={'emailTempPerson_view'}>
                  <TableTabButton
                    selected={!isSystem}
                    onClick={() => {
                      navigate('/phish_template_email/individual', { replace: true })
                      setSelectedLabel([])
                      setLabelPage(0)
                    }}
                    label={t('menu.individual')}
                  />
                </PermissionsController>
              </Stack>
            </Grid>
          </Grid>
          <Table
            minHeight={400}
            columns={cols}
            rows={data || []}
            dataGridConfig={{
              sortingMode: 'server',
              sortingOrder: ['desc', 'asc'],
              sortModel: sort,
              onSortModelChange: newSort => setSort(newSort),
              getRowId: row => row.emailTempId,
              loading: isLoading,
            }}
            noRowsLabel={
              selectedLabel
                ? t('errors.no_templates_under_label')
                : t('errors.no_phish_email_templates')
            }
            pagination={pagination}
          />
        </Grid>
      </Grid>

      <EmailTemplatePreviewDialog
        open={!!previewEmailTemplateId}
        onClose={() => {
          setPreviewEmailTemplateId()
        }}
        emailTempId={previewEmailTemplateId}
      />

      <ConfirmDialog
        open={!!deleteEmailTemplateData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteEmailTemplateData()
        }}
        onSubmit={() => {
          deleteEmailTemplate()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteEmailTemplateData?.name}${t('messages.info')}?`}</Typography>
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!deleteLabelData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteLabelData()
        }}
        onSubmit={() => {
          deleteEmailTemplateLabel()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteLabelData?.labelName}${t('messages.info')}?`}</Typography>
        </Stack>
      </ConfirmDialog>
    </Container>
  )
}

export default connect(mapStateToProps)(PhishEmailTemplate)
